import { Link } from '@tanstack/react-router'
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search } from 'lucide-react'
import { useOrders } from '@/api/hooks'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { formatMoney, formatPercent, formatStatus, parseUTCDate, resultCount } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'

const STATUS_OPTIONS = ['all', 'pending', 'confirmed', 'processing', 'fulfilled', 'cancelled']

export function OrdersPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ status, page }, setFilters] = useQueryStates({
    status: parseAsString.withDefault('all'),
    page: parseAsInteger.withDefault(1),
  })

  const { opsPersonId } = useMyProjectsFilter()
  const { data, isLoading } = useOrders({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    ops_person_id: opsPersonId,
    page,
    limit: 20,
  })

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Sales Orders</h1>

      <ListFilters

        count={resultCount(data?.total, 'order')}
        active={status === 'all' ? 0 : 1}
        onClear={() => void setFilters({ status: null, page: 1 })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search orders..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Status">
          <Select
            value={status}
            onValueChange={(v) => {
              void setFilters({ status: v, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Order status filter" className="w-full">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === 'all' ? 'All Statuses' : s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {/* Desktop table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Financial</TableHead>
              <TableHead>Fulfillment</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-right">Profit</TableHead>
              <TableHead className="text-right">Margin %</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data?.items?.length ? (
              data.items.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: order.uuid }}
                      className="font-medium text-primary hover:underline"
                    >
                      {order.name || order.order_number || `#${order.id}`}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {order.customer_group ? (
                      <Link
                        to="/customers/$groupId"
                        params={{ groupId: order.customer_group.uuid }}
                        className="text-primary hover:underline"
                      >
                        {order.customer_name || order.customer_group.name}
                      </Link>
                    ) : (
                      order.customer_name || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
                      {formatStatus(order.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {order.financial_status ? (
                      <Badge variant="outline">{formatStatus(order.financial_status)}</Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {order.fulfillment_status ? (
                      <Badge variant="outline">{formatStatus(order.fulfillment_status)}</Badge>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell className="text-right">{formatMoney(order.financials.revenue)}</TableCell>
                  <TableCell className="text-right">{formatMoney(order.financials.profit)}</TableCell>
                  <TableCell className="text-right">
                    {formatPercent(order.financials.margin_percent)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {parseUTCDate(order.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No orders found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Loading...</p>
        ) : data?.items?.length ? (
          data.items.map((order) => (
            <Link
              key={order.id}
              to="/orders/$orderId"
              params={{ orderId: order.uuid }}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-primary">
                    {order.name || order.order_number || `#${order.id}`}
                  </p>
                  {(order.customer_name || order.customer_group?.name) && (
                    <p className="truncate text-xs text-muted-foreground">
                      {order.customer_name || order.customer_group?.name}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold">{formatMoney(order.financials.revenue)}</p>
                  {order.financials.margin_percent != null && (
                    <p className="text-xs text-muted-foreground">
                      {formatPercent(order.financials.margin_percent)} margin
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'} className="text-[10px]">
                  {formatStatus(order.status)}
                </Badge>
                {order.financial_status && (
                  <Badge variant="outline" className="text-[10px]">
                    {formatStatus(order.financial_status)}
                  </Badge>
                )}
                {order.fulfillment_status && (
                  <Badge variant="outline" className="text-[10px]">
                    {formatStatus(order.fulfillment_status)}
                  </Badge>
                )}
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {parseUTCDate(order.created_at).toLocaleDateString()}
                </span>
              </div>
            </Link>
          ))
        ) : (
          <p className="py-4 text-center text-sm text-muted-foreground">No orders found</p>
        )}
      </div>

      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
